/**
 * ArTable XLSX Export
 *
 * Generates a single-sheet Excel file from ArTable data and triggers the browser
 * download. Backs the "Export This Table as XLSX" menu action.
 *
 * `visibleRowIds`, `isCellSuppressed` and `bundleTypeByHeaderRowId` arrive already
 * resolved by `useArTableExport` through `resolveArTableBundleExportVisibility`,
 * so kit/group member rows and cells the active view withholds never reach the
 * sheet, and bundle-header rows export through `createBundleHeaderCellReader`
 * instead of their raw cells (SYD-2276/2279).
 *
 * Columns are filtered by the active visibility mode here; the per-column shape —
 * time-range split, cell type, width — and every formula value come from
 * `buildArTableSheetSchema`, shared with the email/form workbook: this path
 * supplies only `formulaConfig`, never a computed value. A table left with no visible column is
 * reported to the user instead of downloading an empty sheet: that is this
 * single-sheet path's own rule, not the shared builder's, which returns an empty
 * schema so the multi-sheet workbook survives such a table.
 */

import { EXPORT_MAX_ROWS } from '#types/System.Parameters.types';
import type {
  ArBundleType,
  ArColumnDefinition,
  ArRowData,
  ArRowId,
  TypeId,
} from '#types/System.Artifacts.types';
import type { ArFormulaEngineConfig } from '#types';
import { UIElementType } from '#types/System.types';
import type { FieldType, VisibilityTypeId } from '#types/System.types';
import type { DocEntry } from '@/store/Store.types';
import { isVisibleInVisibilityMode } from '@/components/rendering/isVisibleInVisibilityMode';
import type { FormattedCellValue } from '@/components/CompositeField/formattedCellValue';
import { coerceArTableSheetCell } from './coerceArTableSheetCell';
import { buildArTableSheetSchema } from './buildArTableSheetSchema';
import type { ArTableSheetRow, ArTableSheetSchemaItem } from './buildArTableSheetSchema';
import { createBundleHeaderCellReader } from './createBundleHeaderCellReader';

type ExportArTableParams = {
  columns: ArColumnDefinition[];
  rowsMap: Map<string, DocEntry<ArRowData>>;
  visibleRowIds: ArRowId[];
  filename?: string;
  sheetName?: string;
  getColumnLabel: (column: ArColumnDefinition) => string;
  /**
   * True when the cell sits on a bundle member row whose column withholds it from
   * the active view. Suppressed cells yield no value at all.
   */
  isCellSuppressed: (rowId: ArRowId, columnId: string) => boolean;
  /**
   * Bundle type of every header row, from the same `classifyArBundleRows` run that
   * produced `isCellSuppressed`. Their cells resolve through
   * `createBundleHeaderCellReader` and never fall through to the generic read, the
   * formula overlay included — the pre-SYD-2276 pass left header docs carrying
   * stale zeros and dotted `{fields}` objects no surface displays.
   */
  bundleTypeByHeaderRowId: ReadonlyMap<ArRowId, ArBundleType>;
  kitCalcDriverTypeIds: ReadonlySet<TypeId>;
  /**
   * Names the formula columns and evaluates them inside `buildArTableSheetSchema`.
   * Required, never defaulted: an omitted config would silently export a table's
   * formula columns blank rather than fail (SYD-2292).
   */
  formulaConfig: ArFormulaEngineConfig;
  visibilityMode?: VisibilityTypeId;
  fieldTypes?: Record<string, FieldType>;
  formatCompositeValue?: (columnTypeId: string, cellValue: unknown) => string | number | FormattedCellValue | undefined;
};

type ExportResult = {
  success: boolean;
  message: string;
  error?: Error;
};

const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const sanitizeExcelSheetName = (name: string): string => {
  const cleaned = name.replace(/[\\/?*[\]:]/g, ' ').trim();
  return cleaned.length > 31 ? cleaned.substring(0, 31) : cleaned;
};

/**
 * Exports ArTable data to XLSX file.
 */
async function exportArTableToExcel(params: ExportArTableParams): Promise<ExportResult> {
  const {
    columns,
    rowsMap,
    visibleRowIds,
    filename = `export-${new Date().toISOString().split('T')[0]}.xlsx`,
    sheetName = 'Sheet1',
    getColumnLabel,
    isCellSuppressed,
    bundleTypeByHeaderRowId,
    kitCalcDriverTypeIds,
    formulaConfig,
    visibilityMode,
    fieldTypes,
    formatCompositeValue,
  } = params;

  try {
    if (!visibleRowIds.length) {
      return { success: false, message: 'No data to export' };
    }

    if (visibleRowIds.length > EXPORT_MAX_ROWS) {
      return {
        success: false,
        message: `Cannot export more than ${EXPORT_MAX_ROWS.toLocaleString()} rows`,
      };
    }

    if (!visibleRowIds.some((rowId) => rowsMap.has(rowId))) {
      return { success: false, message: 'No row data found' };
    }

    const visibleColumns = columns.filter((col) => {
      if (visibilityMode === undefined || visibilityMode === 'internal') return true;

      if (Array.isArray(col.visibilityTypes) && col.visibilityTypes.includes('hidden')) return false;

      if (!isVisibleInVisibilityMode({ visibilityMode, visibilityTypes: col.visibilityTypes })) return false;

      if (col.uiElementType === UIElementType.Field) {
        const ft = fieldTypes?.[col.typeId];
        if (!isVisibleInVisibilityMode({ visibilityMode, visibilityTypes: ft?.visibilityTypes })) return false;
      }

      return true;
    });

    if (!visibleColumns.length) {
      return { success: false, message: 'No visible columns to export' };
    }

    /**
     * Built over `visibleColumns`, never the unfiltered list: "first column" means
     * the first column this sheet prints, matching what the grid renders at index 0.
     */
    const getBundleHeaderCell = createBundleHeaderCellReader({
      columns: visibleColumns,
      rowsMap,
      bundleTypeByHeaderRowId,
      kitCalcDriverTypeIds,
    });

    const { rows, schema } = buildArTableSheetSchema({
      columns: visibleColumns,
      visibleRowIds,
      rowsMap,
      getColumnLabel,
      isCellSuppressed,
      getBundleHeaderCell,
      formulaConfig,
      fieldTypes,
      formatCompositeValue,
    });

    const truncatedSheetName = sheetName.length > 31 ? sheetName.substring(0, 31) : sheetName;
    const blob = await createExcelWorkbookBlobFromSchema({
      rows,
      schema,
      sheetName: truncatedSheetName,
    });

    const url = window.URL.createObjectURL(blob);
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
      link.click();
    } finally {
      window.URL.revokeObjectURL(url);
    }

    return {
      success: true,
      message: `Exported ${rows.length.toLocaleString()} rows`,
    };
  } catch (error) {
    console.error('ArTable export failed:', error);
    return {
      success: false,
      message: 'Export failed',
      error: error as Error,
    };
  }
}

export { exportArTableToExcel };
export type { ExportArTableParams, ExportResult };

async function createExcelWorkbookBlobFromSchema(params: {
  rows: readonly ArTableSheetRow[];
  schema: readonly ArTableSheetSchemaItem[];
  sheetName: string;
}): Promise<Blob> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();

  const worksheet = workbook.addWorksheet(sanitizeExcelSheetName(params.sheetName) || 'Sheet1');
  worksheet.addRow(params.schema.map((col) => col.column));

  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { wrapText: true, vertical: 'top' };

  for (let i = 0; i < params.schema.length; i++) {
    const schemaItem = params.schema[i];
    const column = worksheet.getColumn(i + 1);
    column.width = schemaItem.width;
    column.alignment = { wrapText: schemaItem.wrap !== false, vertical: 'top' };
    if (schemaItem.type === Date) {
      column.numFmt = schemaItem.format ?? 'MM/DD/YYYY HH:mm:ss';
    }
  }

  for (const row of params.rows) {
    const cells = params.schema.map((schemaItem) =>
      coerceArTableSheetCell(schemaItem, schemaItem.value(row)));
    const addedRow = worksheet.addRow(cells.map((cell) => cell.value));
    cells.forEach((cell, index) => {
      if (cell.numFmt) addedRow.getCell(index + 1).numFmt = cell.numFmt;
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer as ArrayBuffer], { type: EXCEL_MIME_TYPE });
}
